import React from 'react';
import { AttributeTextValue, CartLine, Customer, SalesLine, SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICultureInfoFormatter } from '@msdyn365-commerce/core-internal';
import { ICheckoutOrderConfirmationResources } from '../../definition-extensions/checkout-order-confirmation.ext.props.autogenerated';
import { chargeLinesLogic, IChargeLinesBreakdownData } from '../../../../utils/chargeLinesHelper';

export interface IOrderConfirmationDetailProps {
    cultureFormatter: ICultureInfoFormatter;
    salesOrder: SalesOrder;
    customer: Customer | undefined;
    deliveryMode: string;
    orderedProducts: SimpleProduct[];
    resources: ICheckoutOrderConfirmationResources;
    chargeLinesBreakdown: IChargeLinesBreakdownData[];
    cartLines: CartLine[];
}

export const renderLineChargeNode = (chargeName: string, chargeAmount: string): React.ReactNode => {
    return (
        <p className='order-confirmation_other-charges'>
            <span className='order-information__copy--bold'>{chargeName}: </span>
            <span className='order-information__copy--regular'>{chargeAmount}</span>
        </p>
    );
};

const getPaymentDetailsAttr = (salesLine?: SalesLine): AttributeTextValue | undefined => {
    const paymentAttr = (salesLine?.AttributeValues as AttributeTextValue[])?.find(attr => attr.Name === 'PaymentDetails');
    return paymentAttr;
};

export const InvoicePaymentConfirmationDetail: React.FC<IOrderConfirmationDetailProps> = ({
    salesOrder,
    cultureFormatter,
    customer,
    deliveryMode,
    orderedProducts,
    resources,
    chargeLinesBreakdown,
    cartLines
}) => {
    const getProductName = (itemId: string): string => orderedProducts.find(p => p.ItemId === itemId)?.Name || '';

    const cartChargeLines = salesOrder?.ChargeLines || [];
    const cartLinesChargeLines = salesOrder?.SalesLines?.flatMap(item => item.ChargeLines || []) || [];
    const allChargeLines = [...cartChargeLines, ...cartLinesChargeLines];

    return (
        <div>
            <div className={'order-confirmation'}>
                <h1 className={'order-confirmation__header'}>{resources.paymentSubmittedHeading}</h1>
                <i className={'order-confirmation__print'} onClick={() => window.print()} />
            </div>

            <div className={'order-information'}>
                <div className='order-information__wrapper'>
                    <div className={'order-information__heading'}>{resources.paymentInformationHeading}</div>
                    <div className='order-information__details'>
                        <div className={'order-information__po-num'}>
                            <p className={'order-information__copy--bold'}>{resources.thankYou}</p>
                        </div>

                        <div className='order-information__date'>
                            <p className={'order-information__copy--bold'}>{resources.paymentDate}</p>
                            <p className={'order-information__copy--regular'}>
                                {cultureFormatter.formatDate(salesOrder.CreatedDateTime!, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: '2-digit'
                                })}
                            </p>
                        </div>
                    </div>
                    {salesOrder.Comment ? (
                        <div className='order-information__details'>
                            <div className={'order-information__comment'}>
                                <p className={'order-information__copy--bold'}>{resources.notes}</p>
                                <p className={'order-information__copy--regular'}>{salesOrder.Comment}</p>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className='order-information__wrapper'>
                    <div className={'order-information__heading'}>{resources.accountInformationHeading}</div>
                    <div className='order-information__details'>
                        <div className={'order-information__acc-name'}>
                            <p className={'order-information__copy--bold'}>{resources.accountName}</p>
                            <p className={'order-information__copy--regular'}>{customer?.FirstName}</p>
                        </div>
                        <div className='order-information__date'>
                            <p className={'order-information__copy--bold'}>{resources.accountNumber}</p>
                            <p className={'order-information__copy--regular'}>{customer?.AccountNumber}</p>
                        </div>
                    </div>
                </div>
            </div>
            <table className={'table'}>
                <thead className={'order-confirmation-header'}>
                    <tr>
                        <th>{resources.line}</th>
                        <th>{resources.itemId}</th>
                        <th>{resources.description}</th>
                        <th>{resources.price}</th>
                        <th>{resources.quantity}</th>
                        <th>{resources.total}</th>
                    </tr>
                </thead>
                <tbody>
                    {salesOrder.SalesLines?.map((so, idx) => {
                        const attr = getPaymentDetailsAttr(so);
                        const amount = attr?.TextValue;
                        return (
                            <tr>
                                <td>{so.LineNumber}</td>
                                <td>{so.ItemId}</td>
                                <td>
                                    {getProductName(so.ItemId!)}
                                    {so.Comment ? (
                                        <>
                                            <span className={'order-information__copy--bold'}>Notes: </span>
                                            <span className={'order-information__copy--regular'}>{so.Comment}</span>
                                        </>
                                    ) : null}
                                </td>
                                <td>{so.OriginalPrice ? cultureFormatter.formatCurrency(so.OriginalPrice) : 'N/A'}</td>
                                <td>{so.Quantity}</td>
                                <td>{so.Comment && amount ? cultureFormatter.formatCurrency(amount) : 'N/A'}</td>
                            </tr>
                        );
                    })}
                </tbody>
                {/* <tbody>
                    {cartLines?.map((cl, idx) => (
                        <tr>
                            <td>{cl.LineNumber}</td>
                            <td>{cl.ItemId}</td>
                            <td>
                                {getProductName(cl.ItemId!)}
                                {cl.Comment ? (
                                    <>
                                        <span className={'order-information__copy--bold'}>Notes: </span>
                                        <span className={'order-information__copy--regular'}>{cl.Comment}</span>
                                    </>
                                ) : null}
                            </td>
                            <td>{cl.OriginalPrice ? cultureFormatter.formatCurrency(cl.OriginalPrice) : 'N/A'}</td>
                            <td>{cl.Quantity}</td>
                            <td>{cl.ExtendedPrice ? cultureFormatter.formatCurrency(cl.ExtendedPrice) : 'N/A'}</td>
                        </tr>
                    ))}
                </tbody> */}
            </table>
            <div className={'order-confirmation__footer'}>
                {salesOrder.ChargeLines &&
                    chargeLinesLogic(
                        chargeLinesBreakdown,
                        cultureFormatter.formatCurrency,
                        allChargeLines,
                        renderLineChargeNode,
                        salesOrder.ChargeAmount || 0
                    )}

                <div className={'order-confirmation_order-total'}>
                    <p className={'order-information__copy--bold'}>{resources.orderTotalAmount}</p>
                    <p className={'order-information__copy--regular'}>
                        {' '}
                        {salesOrder.TotalAmount ? cultureFormatter.formatCurrency(salesOrder.TotalAmount) : 'N/A'}
                    </p>
                </div>
            </div>
        </div>
    );
};
